// types
import { PermissionOperation, PermissionResource, Permissions } from '../@types';

export const permissionOperationMap: Record<PermissionOperation, string> = {
	APPROVE: 'approve',
	CREATE: 'create',
	DELETE: 'delete',
	READ: 'read',
	READ_PII: 'readPii',
	UPDATE: 'update',
};

export const permissionResourceMap: Record<PermissionResource, string> = {
	ACCOUNT: 'account',
	ACT: 'act',
	BENEFICIARY: 'beneficiary',
	BENEFIT: 'benefit',
	CASH_BENEFIT: 'cashBenefit',
	CASH_IN_METHOD: 'cashInMethod',
	CATEGORY: 'category',
	COMMENT: 'comment',
	COMPONENT: 'component',
	CONTRACT: 'contract',
	DOCUMENT: 'document',
	ENTITY: 'entity',
	EXPENSE_TYPE: 'expenseType',
	FORM: 'form',
	INVOICE: 'invoice',
	LOAN: 'loan',
	NON_CASH_BENEFIT: 'nonCashBenefit',
	OPEX: 'opEx',
	PAYMENT: 'payment',
	PROGRAM: 'program',
	RECONCILIATION_ITEM: 'reconciliationItem',
	REIMBURSEMENT: 'reimbursement',
	REPLENISHMENT: 'replenishment',
	SUPPLIER: 'supplier',
	TAG: 'tag',
	TRANSFER: 'transfer',
	USER: 'user',
	WITHDRAWAL_APPLICATION: 'withdrawalApplication',
};

export const operations = Object.values(permissionOperationMap);
export const resources = Object.values(permissionResourceMap);

export interface ConstructPermissionsProps {
	template: 'ADMIN' | 'BENEFICIARY' | 'FNFA' | 'READ_ONLY' | 'READ_WRITE' | 'SUPPLIER';
}

export const constructPermissions = ({ template }: ConstructPermissionsProps) => {
	// filter out readPii from operations
	const operationsFiltered = operations.filter((operation) => {
		return operation !== 'readPii';
	});

	let permissions: Permissions = {};

	if (template === 'ADMIN') {
		resources.forEach((resource) => {
			operationsFiltered.forEach((operation) => {
				permissions = {
					...permissions,
					[resource]: {
						...permissions[resource],
						[operation]: true,
					},
				};
			});
		});
	} else if (template === 'BENEFICIARY') {
		resources.forEach((resource) => {
			operationsFiltered.forEach((operation) => {
				permissions = {
					...permissions,
					[resource]: {
						...permissions[resource],
						[operation]:
							(resource === 'cashBenefit' && operation === 'read') ||
							(resource === 'cashBenefit' && operation === 'update') ||
							(resource === 'document' && operation === 'read') ||
							(resource === 'document' && operation === 'create') ||
							(resource === 'document' && operation === 'update') ||
							(resource === 'beneficiary' && operation === 'read') ||
							(resource === 'benefit' && operation === 'read') ||
							(resource === 'entity' && operation === 'read') ||
							(resource === 'expenseType' && operation === 'read') ||
							(resource === 'nonCashBenefit' && operation === 'read') ||
							(resource === 'project' && operation === 'read') ||
							(resource === 'supplier' && operation === 'read') ||
							(resource === 'user' && operation === 'read'),
					},
				};
			});
		});
	} else if (template === 'FNFA') {
		resources.forEach((resource) => {
			operationsFiltered.forEach((operation) => {
				permissions = {
					...permissions,
					[resource]: {
						...permissions[resource],
						[operation]:
							operation === 'read' ||
							(resource === 'benefit' && operation === 'create') ||
							(resource === 'benefit' && operation === 'update') ||
							(resource === 'cashInMethod' && operation === 'create') ||
							(resource === 'cashInMethod' && operation === 'update') ||
							(resource === 'category' && operation === 'create') ||
							(resource === 'category' && operation === 'update') ||
							(resource === 'component' && operation === 'create') ||
							(resource === 'component' && operation === 'update') ||
							(resource === 'expenseType' && operation === 'create') ||
							(resource === 'expenseType' && operation === 'update') ||
							(resource === 'program' && operation === 'create') ||
							(resource === 'program' && operation === 'update') ||
							(resource === 'reconciliationItem' && operation === 'create') ||
							(resource === 'reconciliationItem' && operation === 'update') ||
							(resource === 'tag' && operation === 'create') ||
							(resource === 'tag' && operation === 'update'),
					},
				};
			});
		});
	} else if (template === 'READ_ONLY') {
		resources.forEach((resource) => {
			operationsFiltered.forEach((operation) => {
				permissions = {
					...permissions,
					[resource]: {
						...permissions[resource],
						[operation]: operation === 'read',
					},
				};
			});
		});
	} else if (template === 'READ_WRITE') {
		resources.forEach((resource) => {
			operationsFiltered.forEach((operation) => {
				permissions = {
					...permissions,
					[resource]: {
						...permissions[resource],
						[operation]: operation !== 'approve',
					},
				};
			});
		});
	} else if (template === 'SUPPLIER') {
		resources.forEach((resource) => {
			operationsFiltered.forEach((operation) => {
				permissions = {
					...permissions,
					[resource]: {
						...permissions[resource],
						[operation]:
							(resource === 'contract' && operation === 'read') ||
							(resource === 'document' && operation === 'read') ||
							(resource === 'document' && operation === 'create') ||
							(resource === 'document' && operation === 'update') ||
							(resource === 'entity' && operation === 'read') ||
							(resource === 'expenseType' && operation === 'read') ||
							(resource === 'invoice' && operation === 'read') ||
							(resource === 'invoice' && operation === 'create') ||
							(resource === 'invoice' && operation === 'delete') ||
							(resource === 'invoice' && operation === 'update') ||
							(resource === 'payment' && operation === 'read') ||
							(resource === 'payment' && operation === 'update') ||
							(resource === 'project' && operation === 'read') ||
							(resource === 'supplier' && operation === 'read') ||
							(resource === 'user' && operation === 'read'),
					},
				};
			});
		});
	}

	return permissions;
};

// permissions templates
export const permissionsAdmin = constructPermissions({ template: 'ADMIN' });
export const permissionsBeneficiary = constructPermissions({ template: 'BENEFICIARY' });
export const permissionsFnFa = constructPermissions({ template: 'FNFA' });
export const permissionsReadOnly = constructPermissions({ template: 'READ_ONLY' });
export const permissionsReadWrite = constructPermissions({ template: 'READ_WRITE' });
export const permissionsSupplier = constructPermissions({ template: 'SUPPLIER' });
